import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Code2,
  BarChart3,
  Users,
  Phone,
  Network,
  Database,
  Presentation,
  Globe,
  ShieldCheck,
  Globe2,
  Workflow,
  HeartPulse,
  Briefcase,
  ShoppingBag,
  Cpu,
  Landmark,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import {
  CORE_SERVICES,
  SERVICES,
  VALUE_PROPS,
  INDUSTRIES,
  REGIONS,
  QUALITY_COMMITMENT,
  STATS,
} from "@/lib/siteData";
import WorldCoverageMap from "@/components/common/WorldCoverageMap";

const iconMap = {
  Code2,
  BarChart3,
  Users,
  Phone,
  Network,
  Database,
  PresentationChart: Presentation,
  Globe,
  ShieldCheck,
  Globe2,
  Workflow,
  HeartPulse,
  Briefcase,
  ShoppingBag,
  Cpu,
  Landmark,
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="home-hero">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-bl from-teal-50 via-transparent to-transparent" />
        </div>

        <div className="vd-container relative pt-20 pb-24 md:pt-28 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-slate-200 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium text-slate-700">
              <Sparkles className="h-3.5 w-3.5 text-orange-600" />
              Trusted · Timely · Thorough
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900">
              Global Research <br className="hidden md:block" /> Execution,
              Delivered with <span className="text-teal-700">Confidence</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
              VeriData Global helps market research agencies, consulting firms,
              and insights teams execute research projects faster, more
              efficiently, and with uncompromising quality. From survey
              programming and respondent recruitment to data processing and
              reporting — end-to-end execution across global markets.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-quote-cta"
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-consult-cta"
                className="inline-flex items-center gap-2 bg-transparent border border-slate-900 text-slate-900 hover:bg-slate-50 font-medium px-6 py-3.5 rounded-md transition-colors"
              >
                Schedule a Consultation
              </Link>
            </div>

            <div
              data-testid="hero-trust-strip"
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-slate-600"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-teal-700" />
                Trusted by agencies & insights teams in 25+ countries
              </div>
              <span className="hidden sm:inline-block h-1 w-1 rounded-full bg-slate-300" />
              <div className="flex items-center gap-2">
                <Workflow className="h-4 w-4 text-teal-700" />
                One partner. Programming to reporting.
              </div>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
              {STATS.map((s) => (
                <div key={s.label} className="bg-white px-5 py-5">
                  <div className="font-display text-2xl md:text-3xl font-semibold text-slate-900">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[12px] uppercase tracking-[0.15em] text-slate-500">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-5 relative">
            <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#050b16] shadow-[0_30px_80px_-20px_rgba(11,25,44,0.45)] p-4">
              {/* Interactive world coverage map */}
              <WorldCoverageMap />

              {/* Core Services overlay */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="vd-eyebrow text-teal-300">Core Services</p>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[13px] text-white/90">
                  {CORE_SERVICES.map((s) => (
                    <div key={s} className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-teal-400 mt-0.5 flex-shrink-0" />
                      <span>{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUSTED PARTNER STORY */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="vd-eyebrow">Trusted Research Execution Partner</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Execution complexity, simplified into one partnership.
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-600 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              Research organizations today face increasing pressure to deliver
              more projects, across more markets, with tighter timelines and
              higher quality expectations. Managing multiple vendors,
              coordinating fieldwork across countries, and maintaining
              consistent quality standards can create operational complexity
              that slows project delivery and increases risk.
            </p>
            <p>
              VeriData Global simplifies research execution by providing a
              single partner for your entire research lifecycle. We work as an
              extension of your team — helping you scale research operations
              without increasing internal overhead.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="border-b border-slate-200 bg-slate-50/60">
        <div className="vd-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="vd-eyebrow">End-to-End Research Services</p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                Eight specialized capabilities. <br /> Designed to operate as one.
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/services"
                data-testid="home-view-services"
                className="inline-flex items-center gap-2 text-slate-900 font-medium border-b border-slate-900 pb-1 hover:border-orange-600 hover:text-orange-700 transition-colors"
              >
                View all services <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {SERVICES.map((s, i) => {
              const Icon = iconMap[s.icon] || Code2;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className="bg-white p-7 group hover:bg-slate-50 transition-colors"
                >
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`home-service-card-${s.slug}`}
                    className="block h-full"
                  >
                    <div className="flex items-start justify-between">
                      <div className="h-11 w-11 rounded-md border border-slate-200 bg-white flex items-center justify-center text-teal-700 group-hover:border-teal-600 group-hover:text-teal-800 transition-colors">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-orange-600 transition-colors" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {s.short}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY VERIDATA - 3 PILLARS */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-28">
          <div className="max-w-3xl">
            <p className="vd-eyebrow">Why VeriData Global</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Built around three uncompromising principles.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {VALUE_PROPS.map((p, i) => {
              const Icon = iconMap[p.icon] || ShieldCheck;
              return (
                <div key={p.title} className="bg-white p-8">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-xs text-slate-400">
                      0{i + 1}
                    </span>
                    <span className="h-px w-10 bg-slate-200" />
                  </div>
                  <Icon className="h-6 w-6 text-teal-700 mt-6" />
                  <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-b border-slate-200 bg-slate-50/60">
        <div className="vd-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="vd-eyebrow">Industries We Serve</p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                Specialized expertise across sectors where research precision matters.
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/industries"
                data-testid="home-view-industries"
                className="inline-flex items-center gap-2 text-slate-900 font-medium border-b border-slate-900 pb-1 hover:border-orange-600 hover:text-orange-700 transition-colors"
              >
                Explore industries <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {INDUSTRIES.map((ind) => {
              const Icon = iconMap[ind.icon] || HeartPulse;
              return (
                <div
                  key={ind.name}
                  className="bg-white border border-slate-200 rounded-md p-6 hover:-translate-y-1 hover:border-teal-600 transition-all duration-300"
                >
                  <Icon className="h-6 w-6 text-orange-600" />
                  <h3 className="mt-4 font-display text-base font-semibold text-slate-900 leading-snug">
                    {ind.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {ind.blurb}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GLOBAL COVERAGE - DARK */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-60 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
                Global Coverage
              </p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight">
                Research execution across four continents.
              </h2>
              <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
                Reach the markets, languages, and audiences that matter to your
                study — through a single partner with native-speaking resources
                and localized project management.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 border border-white/15 rounded-md px-4 py-3">
                <Globe className="h-5 w-5 text-teal-400" />
                <span className="text-sm text-white/90">
                  20+ languages · 25+ countries · Native local resources
                </span>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
                {REGIONS.map((r) => {
                  const hasPlus =
                    r.region === "Asia-Pacific" || r.region === "Europe";
                  return (
                    <div key={r.region} className="bg-[#0b192c] p-4">
                      <p className="vd-eyebrow text-teal-400 text-[10px]">
                        {r.region}
                      </p>
                      <p className="mt-1.5 font-display text-lg font-semibold text-white">
                        {r.countries.length}
                        {hasPlus && (
                          <span className="text-orange-400">+</span>
                        )}
                      </p>
                      <p className="text-[11px] text-white/60 uppercase tracking-wider">
                        Countries
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="rounded-md border border-white/10 bg-[#0a1626]/60 p-4 md:p-6">
                <WorldCoverageMap />
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {REGIONS.flatMap((r) => r.countries).map((c) => (
                  <span
                    key={c}
                    className="text-[12px] text-white/75 border border-white/10 rounded-full px-3 py-1 hover:border-teal-400 hover:text-white transition-colors"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUALITY & COMPLIANCE */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="vd-eyebrow">Quality & Compliance</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Quality is not an outcome. It is a process.
            </h2>
            <p className="mt-5 text-slate-600 text-base leading-relaxed">
              VeriData Global incorporates quality validation throughout every
              stage of the research lifecycle — from respondent verification
              through to final data delivery.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {QUALITY_COMMITMENT.map((q) => (
                <div
                  key={q}
                  className="flex items-center gap-3 border border-slate-200 rounded-md px-4 py-4 bg-white hover:border-teal-600 transition-colors"
                >
                  <CheckCircle2 className="h-5 w-5 text-teal-700 flex-shrink-0" />
                  <span className="text-[15px] text-slate-800">{q}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-50 pointer-events-none" />
        <div className="vd-container relative py-24 md:py-32 text-center">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
            Let’s build better research together
          </p>
          <h2 className="mt-6 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] max-w-4xl mx-auto">
            Looking for a research execution partner you can rely on?
          </h2>
          <p className="mt-6 text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you require support for a single study or ongoing research
            programs, VeriData Global provides the expertise, infrastructure,
            and responsiveness needed to deliver successful outcomes.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="home-final-cta-consult"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-7 py-3.5 rounded-md transition-colors shadow-sm"
            >
              Request a Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="home-final-cta-discuss"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-medium px-7 py-3.5 rounded-md transition-colors"
            >
              Discuss Your Next Project
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
